/**
 * "O que fazer agora": a lista de insights acionaveis, em ordem de urgencia.
 *
 * Decisoes nao obvias:
 *  1. Cada item e um `Panel` com o tom do insight, numero de ordem, titulo
 *     com o numero que o justifica, uma frase de contexto e UM botao de acao
 *     quando existe destino. Sem botao quando a acao esta em outra parte da
 *     propria pagina (o seletor de responsavel, por exemplo) -- o texto diz
 *     onde.
 *  2. O vazio e uma boa noticia escrita, nao um cartao em branco: "nada
 *     urgente" tambem e informacao, e diz que o motor continua sozinho.
 */

import { Icon, LinkButton, Panel, SectionCard, type BadgeTone } from '../../../components/ui';
import type { Insight } from '../lib/insights';

const TOM_DO_PAINEL: Readonly<Record<BadgeTone, 'surface' | 'elevated' | 'brand' | 'good' | 'warning' | 'critical' | 'accent'>> = {
  neutral: 'surface',
  brand: 'brand',
  accent: 'accent',
  info: 'surface',
  good: 'good',
  warning: 'warning',
  critical: 'critical',
  violet: 'surface',
};

const TOM_DO_ICONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink/5 text-ink-muted',
  brand: 'bg-brand/10 text-brand',
  accent: 'bg-accent/10 text-accent',
  info: 'bg-info/10 text-info',
  good: 'bg-status-good/10 text-status-good',
  warning: 'bg-status-warning/15 text-status-warning',
  critical: 'bg-status-critical/10 text-status-critical',
  violet: 'bg-series-7/10 text-series-7',
};

export function OQueFazerAgora({
  insights,
  carregando,
  className = '',
}: {
  readonly insights: readonly Insight[];
  readonly carregando: boolean;
  readonly className?: string;
}): JSX.Element {
  return (
    <SectionCard
      id="o-que-fazer-agora"
      title="O que fazer agora"
      icon="clique"
      description="Sugestões derivadas dos números do período e do retrato de agora. Cada uma diz de onde veio."
      className={className}
    >
      {carregando ? (
        <ul aria-hidden="true" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-20 animate-pulse rounded-2xl border border-line bg-elevated" />
          ))}
        </ul>
      ) : insights.length === 0 ? (
        <div className="flex flex-col gap-1 rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center">
          <p className="text-sm font-medium text-ink">Nada urgente por aqui.</p>
          <p className="text-sm text-ink-muted">
            Funis saudáveis e ninguém esperando o comercial. O motor continua agindo sozinho depois de cada abandono ou vencimento; volte amanhã.
          </p>
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {insights.map((insight, i) => (
            <li key={insight.id}>
              <Panel tone={TOM_DO_PAINEL[insight.tone]} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span aria-hidden="true" className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${TOM_DO_ICONE[insight.tone]}`}>
                    <Icon name={insight.icone} size="sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      <span className="mr-1.5 tabular-nums text-ink-subtle">{i + 1}.</span>
                      {insight.titulo}
                    </p>
                    <p className="mt-0.5 text-sm text-ink-muted">{insight.texto}</p>
                  </div>
                </div>
                {insight.acao === null ? null : (
                  <LinkButton to={insight.acao.rota} variant="secondary" size="sm" iconRight="seta-dir" className="shrink-0">
                    {insight.acao.rotulo}
                  </LinkButton>
                )}
              </Panel>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}
